import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The detail panel that slides in over a list — the reference's order drawer.
 *
 * It deliberately does *not* cover the list: the row you opened stays visible
 * and tinted green beside it, so you can work down a list without losing your
 * place. On a phone there is no room for that, so it becomes a full-screen
 * sheet instead.
 *
 * `footer` is sticky, because the number a vendor is looking for (a total, a
 * confirm button) must not require scrolling to the end of a long panel.
 */

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Icon buttons on the trailing edge of the header — edit, print, delete. */
  actions?: React.ReactNode;
  /** Tab row directly under the header. */
  tabs?: React.ReactNode;
  footer?: React.ReactNode;
  width?: 'md' | 'lg';
  children: React.ReactNode;
}

const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onClose,
  title,
  subtitle,
  actions,
  tabs,
  footer,
  width = 'md',
  children,
}) => {
  // Escape closes, and the page behind must not scroll under the panel.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-flup-ink/20 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />

      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative flex flex-col h-full bg-flup-surface shadow-flup-panel animate-slide-in',
          'w-full sm:rounded-l-flup-xl',
          width === 'lg' ? 'sm:w-[560px]' : 'sm:w-[460px]',
        )}
      >
        <header className="flex items-start gap-3 px-5 py-4 border-b border-flup-hairline shrink-0">
          <div className="min-w-0 flex-1">
            <Text variant="h2" as="h2" lines={1}>
              {title}
            </Text>
            {subtitle ? (
              <Text variant="caption" tone="tertiary" as="p" className="mt-0.5">
                {subtitle}
              </Text>
            ) : null}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid place-items-center w-8 h-8 rounded-flup-xs text-flup-ink-3 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
            >
              <X className="w-4 h-4" aria-hidden />
            </button>
          </div>
        </header>

        {tabs ? <div className="px-5 border-b border-flup-hairline shrink-0">{tabs}</div> : null}

        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>

        {footer ? (
          <footer className="px-5 py-4 border-t border-flup-hairline bg-flup-surface shrink-0">
            {footer}
          </footer>
        ) : null}
      </aside>
    </div>
  );
};

/** Underlined tabs for inside a panel — "Order info / Route info". */
export const PanelTabs: React.FC<{
  items: { key: string; label: React.ReactNode }[];
  value: string;
  onChange: (key: string) => void;
}> = ({ items, value, onChange }) => (
  <div role="tablist" className="flex items-center gap-5 -mb-px">
    {items.map((item) => {
      const active = item.key === value;
      return (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          className={cn(
            'relative py-3 transition-colors focus-visible:outline-none',
            active ? 'text-flup-ink' : 'text-flup-ink-3 hover:text-flup-ink-2',
          )}
        >
          <Text variant="labelSemi" tone="inherit" as="span">
            {item.label}
          </Text>
          {active ? (
            <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-pill bg-flup-green" aria-hidden />
          ) : null}
        </button>
      );
    })}
  </div>
);

export default React.memo(SidePanel);
